import { CdkDropList, DragDropModule } from '@angular/cdk/drag-drop';
import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withJsonpSupport } from '@angular/common/http';
import '@angular/common/locales/global/nl';
import localeNL from '@angular/common/locales/nl';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GoogleMapsModule } from '@angular/google-maps';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DATE_LOCALE, MatNativeDateModule, MatRippleModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule, HAMMER_GESTURE_CONFIG, HammerGestureConfig } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { VirtualScrollerModule } from '@iharbeck/ngx-virtual-scroller';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { QRCodeComponent } from 'angularx-qrcode';
import { DndModule } from 'ngx-drag-drop';
import { UiScrollModule } from 'ngx-ui-scroll';
import { lastValueFrom } from 'rxjs';
import { register } from 'swiper/element/bundle';
import { AddressAreaComponent } from './address-area/address-area.component';
import { AddressesMapComponent } from './addresses-map/addresses-map.component';
import { AirtableMapComponent } from './airtable-map/airtable-map.component';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BackButtonService } from './back-button.service';
import { BackButtonComponent } from './back-button/back-button.component';
import { CameraService } from './camera.service';
import { CanDeactivateForm } from './can-deactivate-form';
import { ChatMessageInfoDialogComponent } from './chat/chat-message-info-dialog/chat-message-info-dialog.component';
import { ChatMessageService } from './chat/chat-message.service';
import { ChatMessagingComponent } from './chat/chat-messaging/chat-messaging.component';
import { ChatOverviewComponent } from './chat/chat-overview/chat-overview.component';
import { ChatSettingsDialogComponent } from './chat/chat-settings-dialog/chat-settings-dialog.component';
import { ChatService } from './chat/chat.service';
import { ChatComponent } from './chat/chat/chat.component';
import { NewChatDialogComponent } from './chat/new-chat-dialog/new-chat-dialog.component';
import { CodaltImageComponent } from './codalt-image/codalt-image.component';
import { CodaltComponent } from './codalt.component';
import { CodaltInterceptor } from './codalt.interceptor';
import { PullToRefreshComponent } from './components/pull-to-refresh/pull-to-refresh.component';
import { ContactDetailComponent } from './contacts/contact-detail/contact-detail.component';
import { ContactEditComponent } from './contacts/contact-edit/contact-edit.component';
import { ContactOverviewComponent } from './contacts/contact-overview/contact-overview.component';
import { ContactPhotoComponent } from './contacts/contact-photo/contact-photo.component';
import { CustomErrorHandlerService } from './custom-error-handler-service';
import { CustomerDetailComponent } from './customer-detail/customer-detail.component';
import { CustomerListComponent } from './customer-list/customer-list.component';
import { CustomerLogoComponent } from './customer-logo/customer-logo.component';
import { CustomerProjectConsumersComponent } from './customer-project-consumers/customer-project-consumers.component';
import { CustomerProjectDescriptionDialogComponent } from './customer-project-detail/customer-project-description-dialog/customer-project-description-dialog.component';
import { CustomerProjectDetailComponent } from './customer-project-detail/customer-project-detail.component';
import { CustomerProjectExportDialogComponent } from './customer-project-detail/customer-project-export-dialog/customer-project-export-dialog.component';
import { CustomerProjectPhaseActivitiesComponent } from './customer-project-detail/customer-project-phase-activities/customer-project-phase-activities.component';
import { CustomerProjectPhaseActivityEditDialogComponent } from './customer-project-detail/customer-project-phase-activities/customer-project-phase-activity-edit-dialog/customer-project-phase-activity-edit-dialog.component';
import { CustomerProjectPhaseEditDialogComponent } from './customer-project-detail/customer-project-phase-list/customer-project-phase-edit-dialog/customer-project-phase-edit-dialog.component';
import { CustomerProjectPhaseListComponent } from './customer-project-detail/customer-project-phase-list/customer-project-phase-list.component';
import { CustomerProjectPhaseRepliesComponent } from './customer-project-detail/customer-project-phase-replies/customer-project-phase-replies.component';
import { CustomerProjectDialogComponent } from './customer-project-manage/customer-project-dialog/customer-project-dialog.component';
import { CustomerProjectFormComponent } from './customer-project-manage/customer-project-form/customer-project-form.component';
import { CustomerProjectManageComponent } from './customer-project-manage/customer-project-manage.component';
import { CustomerProjectOverviewComponent } from './customer-project-overview/customer-project-overview.component';
import { CustomerProjectRequestAccessComponent } from './customer-project-request-access/customer-project-request-access.component';
import { CustomerReportComponent } from './customer-report/customer-report.component';
import { CustomerTableComponent } from './customer-table/customer-table.component';
import { CustomersOverviewComponent } from './customers-overview/customers-overview.component';
import { DecimalInputDirective } from './directives/decimal-input.directive';
import { DocumentUploadComponent } from './document-upload/document-upload.component';
import { AddDocumentComponent } from './documents/add-chapter/add-document.component';
import { DocumentsComponent } from './documents/documents.component';
import { EditDocumentDetailsComponent } from './documents/edit-document-details/edit-document-details.component';
import { ImageViewerDialogComponent } from './documents/image-viewer-dialog/image-viewer-dialog.component';
import { PdfViewerDialogComponent } from './documents/pdf-viewer-dialog/pdf-viewer-dialog.component';
import { RenameDocumentComponent } from './documents/rename-chapter/rename-document.component';
import { FieldErrorComponent } from './field-error/field-error.component';
import { ChooseFileSourceComponent } from './file-manager/choose-file-source/choose-file-source.component';
import { FileManagerComponent } from './file-manager/file-manager.component';
import { FileSelectionDialogComponent } from './file-manager/file-selection-dialog/file-selection-dialog.component';
import { FirebaseService } from './firebase.service';
import { GpsService } from './gps.service';
import { ImageViewerComponent } from './image-viewer/image-viewer.component';
import { ImportExcelComponent } from './import-excel/import-excel.component';
import { ImportShapefileComponent } from './import-shapefile/import-shapefile.component';
import { LoginCodeComponent } from './login-code/login-code.component';
import { LoginComponent } from './login/login.component';
import { LogoutComponent } from './logout/logout.component';
import { MapMeasurementsComponent } from './map-measurements/map-measurements.component';
import { WorkareaTransferDialogComponent } from './map-measurements/workarea-transfer-dialog/workarea-transfer-dialog.component';
import { MicrosoftCallbackComponent } from './microsoft-callback/microsoft-callback.component';
import { NewsDetailComponent } from './news/news-detail/news-detail.component';
import { NewsEditComponent } from './news/news-edit/news-edit.component';
import { NewsComponent } from './news/news.component';
import { ByIdPipe } from './pipes/by-id.pipe';
import { ChatTypePipe } from './pipes/chat-type-icon.pipe';
import { FilterPipe } from './pipes/filter.pipe';
import { FormattedAreaPipe } from './pipes/formatted-area.pipe';
import { FormattedDistancePipe } from './pipes/formatted-distance.pipe';
import { GeojsonBoundsPipe } from './pipes/geojson-bounds.pipe';
import { GeojsonToPathsPipe } from './pipes/geojson-to-paths.pipe';
import { HasFeaturePipe } from './pipes/has-feature.pipe';
import { HasFunctionalityPipe } from './pipes/has-functionality.pipe';
import { HasRolePipe } from './pipes/has-role.pipe';
import { HectoPinIconPipe } from './pipes/hecto-pin-icon.pipe';
import { IsBase64Pipe } from './pipes/is-base64.pipe';
import { LatLngPositionPipe } from './pipes/lat-lng-position.pipe';
import { MapBoundsContainsPipe } from './pipes/map-bounds-contains.pipe';
import { MapMarkerPipe } from './pipes/map-marker.pipe';
import { MarkerPipe } from './pipes/marker.pipe';
import { Nl2brPipe } from './pipes/nl2br.pipe';
import { NotByIdPipe } from './pipes/not-by-id.pipe';
import { ProfilePictureUrlPipe } from './pipes/profile-picture-url.pipe';
import { ProjecttypePipe } from './pipes/projecttype.pipe';
import { SafeBgUrlPipe } from './pipes/safe-bg-url.pipe';
import { SafeHtml } from './pipes/safe-html.pipe';
import { SafeSrcPipe } from './pipes/safe-src.pipe';
import { SelectedOptionsPipe } from './pipes/selected-options.pipe.ts';
import { ShortNumberPipe } from './pipes/short-number.pipe';
import { StatusDisabledPipe } from './pipes/status-disabled.pipe';
import { WhereGreaterOrEqualPipe } from './pipes/whereGreaterOrEqual.pipe';
import { WhereLessPipe } from './pipes/whereLess.pipe';
import { WorkareaClusterPipe } from './pipes/workarea-cluster.pipe';
import { WorkareaMarkerPipe } from './pipes/workarea-marker.pipe';
import { WorkareaPolygonOptionsPipe } from './pipes/workarea-polygon-options.pipe';
import { WorkareasBoundsCountPipe } from './pipes/workareas-bounds-count.pipe';
import { ProcessbarComponent } from './processbar/processbar.component';
import { ProjectsComponent } from './projects/projects.component';
import { QrComponent } from './qr/qr.component';
import { RegisterComponent } from './register/register.component';
import { AddressService } from './services/address/address.service';
import { AirtableService } from './services/airtable/airtable.service';
import { ApiService } from './services/api/api.service';
import { AuthService } from './services/auth/auth.service';
import { AuthorisationService } from './services/auth/authorisation.service';
import { ConfirmDialogComponent } from './services/confirm-dialog-service/confirm-dialog/confirm-dialog.component';
import { CustomerProjectPhaseService } from './services/customer-project/customer-project-phase.service';
import { CustomerProjectService } from './services/customer-project/customer-project.service';
import { CustomerService } from './services/customer/customer.service';
import { ExportService } from './services/export/export.service';
import { FileService } from './services/file.service';
import { LoggerService } from './services/logger.service';
import { NativeFileDownloadDialogComponent } from './services/native-file-viewer/native-file-download-dialog.component';
import { NewsService } from './services/news.service';
import { PageTitleService } from './services/page-title/page-title.service';
import { SurveyService } from './services/survey.service';
import { TimelineService } from './services/timeline.service';
import { UserService } from './services/user/user.service';
import { AddCustomerProjectModalComponent } from './settings/add-customer-project-modal/add-customer-project-modal.component';
import { AuthSessionsComponent } from './settings/auth-sessions/auth-sessions.component';
import { SettingsWfsComponent } from './settings/settings-wfs/settings-wfs.component';
import { SettingsComponent } from './settings/settings.component';
import { FeaturePreviewDialogComponent } from './shared/feature-preview-dialog/feature-preview-dialog.component';
import { SimpleTinyComponent } from './simple-tiny/simple-tiny.component';
import { SurveyAnswerComponent } from './survey/survey-answer/survey-answer.component';
import { SurveyAnswersComponent } from './survey/survey-answers/survey-answers.component';
import { SurveyEditComponent } from './survey/survey-edit/survey-edit.component';
import { TakeOverComponent } from './take-over/take-over.component';
import { CustomerTimelineComponent } from './timeline/customer-timeline/customer-timeline.component';
import { TimelineDetailComponent } from './timeline/timeline-detail/timeline-detail.component';
import { TimelineEditComponent } from './timeline/timeline-edit/timeline-edit.component';
import { TimelineComponent } from './timeline/timeline/timeline.component';
import { WherePipe } from './where.pipe';
import { LocationSelectionDialogComponent } from './workarea/location-selection-dialog/location-selection-dialog.component';
import { LocationService } from './workarea/shared/location.service';
import { WorkareaDrillComponent } from './workarea/workarea-drill/workarea-drill.component';
import { WorkareaGeneralComponent } from './workarea/workarea-general/workarea-general.component';
import { WorkareaHistoryDialogComponent } from './workarea/workarea-history-dialog/workarea-history-dialog.component';
import { WorkareaNawComponent } from './workarea/workarea-naw/workarea-naw.component';
import { WorkareaStraatwerkComponent } from './workarea/workarea-straatwerk/workarea-straatwerk.component';
import { WorkareaComponent } from './workarea/workarea.component';
import { WorkareasFilterComponent } from './workareas-filter/workareas-filter.component';
import { WorkareaProjectChooseDialogComponent } from './workareas-map/workarea-type-choose-dialog/workarea-project-choose-dialog.component';
import { WorkareasMapComponent } from './workareas-map/workareas-map.component';

register();
registerLocaleData(localeNL, 'nl');

declare var Hammer: any;

export function initializeAppFactory(authService: AuthorisationService): () => Promise<any> {
    return () => lastValueFrom(authService.rightsMap());
}

export class MyHammerConfig extends HammerGestureConfig {
    buildHammer(element: HTMLElement) {
        let mc = new Hammer(element, {
            touchAction: 'pan-y',
        });
        return mc;
    }
}

@NgModule({
    declarations: [
        AppComponent,
        LoginCodeComponent,
        LoginComponent,
        SettingsComponent,
        AuthSessionsComponent,
        ProcessbarComponent,
        WherePipe,
        Nl2brPipe,
        WorkareasMapComponent,
        WorkareaComponent,
        AddressesMapComponent,
        SafeBgUrlPipe,
        ProfilePictureUrlPipe,
        ProjecttypePipe,
        ChatTypePipe,
        SafeHtml,
        BackButtonComponent,
        ContactOverviewComponent,
        ContactDetailComponent,
        ContactEditComponent,
        ContactPhotoComponent,
        RegisterComponent,
        ImageViewerComponent,
        PullToRefreshComponent,
        WorkareaNawComponent,
        WorkareaGeneralComponent,
        CustomersOverviewComponent,
        WorkareasFilterComponent,
        WorkareaStraatwerkComponent,
        DocumentsComponent,
        AddDocumentComponent,
        AirtableMapComponent,
        PdfViewerDialogComponent,
        RenameDocumentComponent,
        ConfirmDialogComponent,
        EditDocumentDetailsComponent,
        ImageViewerDialogComponent,
        CustomerProjectOverviewComponent,
        CustomerProjectDetailComponent,
        CustomerDetailComponent,
        WorkareaDrillComponent,
        CustomerDetailComponent,
        CustomerProjectManageComponent,
        CustomerProjectDialogComponent,
        CustomerProjectFormComponent,
        CustomerProjectRequestAccessComponent,
        FieldErrorComponent,
        CustomerListComponent,
        CustomerTableComponent,
        LogoutComponent,
        WorkareaProjectChooseDialogComponent,
        DocumentUploadComponent,
        SimpleTinyComponent,
        CustomerReportComponent,
        CustomerLogoComponent,
        CustomerProjectPhaseListComponent,
        CustomerProjectPhaseEditDialogComponent,
        CustomerProjectPhaseActivitiesComponent,
        CustomerProjectPhaseActivityEditDialogComponent,
        MarkerPipe,
        FormattedAreaPipe,
        FormattedDistancePipe,
        CustomerProjectPhaseRepliesComponent,
        DecimalInputDirective,
        ChatOverviewComponent,
        ChatMessagingComponent,
        ChatMessageInfoDialogComponent,
        ChatSettingsDialogComponent,
        IsBase64Pipe,
        LatLngPositionPipe,
        MapBoundsContainsPipe,
        WorkareasBoundsCountPipe,
        HectoPinIconPipe,
        WhereLessPipe,
        NotByIdPipe,
        ByIdPipe,
        WhereGreaterOrEqualPipe,
        CodaltImageComponent,
        SafeSrcPipe,
        NewChatDialogComponent,
        ChatComponent,
        FilterPipe,
        ProjectsComponent,
        ProjecttypePipe,
        AddCustomerProjectModalComponent,
        NewsEditComponent,
        NewsComponent,
        NewsDetailComponent,
        CodaltComponent,
        FileManagerComponent,
        FileSelectionDialogComponent,
        ChooseFileSourceComponent,
        TimelineComponent,
        CustomerTimelineComponent,
        TimelineEditComponent,
        TimelineDetailComponent,
        QrComponent,
        CustomerProjectDescriptionDialogComponent,
        WorkareaHistoryDialogComponent,
        LocationSelectionDialogComponent,
        StatusDisabledPipe,
        SurveyAnswerComponent,
        SurveyAnswersComponent,
        SurveyEditComponent,
        ShortNumberPipe,
        SelectedOptionsPipe,
        CustomerProjectConsumersComponent,
        CustomerProjectExportDialogComponent,
        WorkareaMarkerPipe,
        WorkareaPolygonOptionsPipe,
        WorkareaClusterPipe,
        GeojsonToPathsPipe,
        GeojsonBoundsPipe,
        MapMarkerPipe,
        MapMeasurementsComponent,
        WorkareaTransferDialogComponent,
        ImportShapefileComponent,
        ImportExcelComponent,
        FeaturePreviewDialogComponent,
        AddressAreaComponent,
        NativeFileDownloadDialogComponent,
        AddressAreaComponent,
        MicrosoftCallbackComponent,
        TakeOverComponent,
        HasFeaturePipe,
        HasFunctionalityPipe,
        HasRolePipe,
    ],
    bootstrap: [AppComponent],
    imports: [
        AppRoutingModule,
        BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        ReactiveFormsModule,
        NgSelectModule,
        SettingsWfsComponent,
        MatButtonModule,
        MatCheckboxModule,
        MatSelectModule,
        MatInputModule,
        MatSlideToggleModule,
        MatDividerModule,
        MatDialogModule,
        MatFormFieldModule,
        DragDropModule,
        MatIconModule,
        MatMenuModule,
        MatAutocompleteModule,
        MatListModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatTableModule,
        MatRadioModule,
        MatProgressBarModule,
        MatProgressSpinnerModule,
        MatRippleModule,
        AngularSvgIconModule.forRoot(),
        UiScrollModule,
        MatSortModule,
        MatTooltipModule,
        DndModule,
        NgxChartsModule,
        GoogleMapsModule,
        VirtualScrollerModule,
        QRCodeComponent,
    ],
    providers: [
        ApiService,
        FileService,
        AuthService,
        UserService,
        BackButtonService,
        AirtableService,
        CustomerService,
        FirebaseService,
        CameraService,
        LocationService,
        ExportService,
        AddressService,
        CustomerProjectService,
        CustomerProjectPhaseService,
        GpsService,
        CdkDropList,
        ChatService,
        ChatMessageService,
        NewsService,
        TimelineService,
        CanDeactivateForm,
        IsBase64Pipe,
        ByIdPipe,
        StatusDisabledPipe,
        MatBottomSheet,
        SurveyService,
        ShortNumberPipe,
        LoggerService,
        PageTitleService,
        { provide: ErrorHandler, useClass: CustomErrorHandlerService },
        { provide: HTTP_INTERCEPTORS, useClass: CodaltInterceptor, multi: true },
        { provide: HAMMER_GESTURE_CONFIG, useClass: MyHammerConfig },
        { provide: MAT_DATE_LOCALE, useValue: 'nl-NL' },
        { provide: LOCALE_ID, useValue: 'nl' },
        {
            provide: APP_INITIALIZER,
            useFactory: initializeAppFactory,
            deps: [AuthorisationService],
            multi: true,
        },
        provideHttpClient(withInterceptorsFromDi(), withJsonpSupport()),
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
